import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../context/UserContext';
import { useLanguage } from '../../../context/LanguageContext';
import './OrderHistoryPage.css';
import OrderDetailsSidebar from '../../../components/common/OrderDetailsSidebar/OrderDetailsSidebar';
import useCurrency from '../../../hooks/useCurrency';
import { getLocalizedText } from '../../../utils/multilingualUtils';
import { getTranslation, getStatusTranslation, getPaymentMethodTranslation } from './translations';
import { getCustomerOrders } from '../../../firebase/order-functions';

const OrderHistory = () => {
    const navigate = useNavigate();
    const { currentUser, activeStoreId } = useUser(); // Get activeStoreId from UserContext
    const { formatDisplay } = useCurrency();
    const { language } = useLanguage(); // Get language context

    // Helper function to get translations
    const t = useCallback((key) => getTranslation(language, key), [language]);

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showDetailsSidebar, setShowDetailsSidebar] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Cleanup effect to ensure body overflow is reset when component unmounts
    useEffect(() => {
        return () => {
            // Reset body overflow when component unmounts
            document.body.style.overflow = "";
        };
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const fetchOrders = async () => {
            if (!currentUser || !activeStoreId) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const ordersData = await getCustomerOrders(currentUser.uid, activeStoreId);
                setOrders(ordersData);
            } catch (err) {
                console.error("Error fetching orders:", err);
                setError(t('failedToLoad'));
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, [currentUser, activeStoreId, t]);

    // Format currency (legacy function for OrderDetailsSidebar)
    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return "N/A";
        return parseFloat(amount || 0).toFixed(2);
    };

    // Format date
    const formatDate = (timestamp) => {
        if (!timestamp) return t('notAvailable');

        let date;
        try {
            // Handle different timestamp formats
            if (timestamp.toDate && typeof timestamp.toDate === 'function') {
                date = timestamp.toDate();
            } else if (timestamp instanceof Date) {
                date = timestamp;
            } else if (typeof timestamp === 'string') {
                date = new Date(timestamp);
            } else if (typeof timestamp === 'number') {
                date = new Date(timestamp);
            } else {
                return t('notAvailable');
            }

            // Always use Gregorian calendar but translate month names for Arabic
            if (language === 'ar') {
                const monthsArabic = [
                    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
                    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
                ];
                const day = date.getDate();
                const month = monthsArabic[date.getMonth()];
                const year = date.getFullYear();
                return `${day} ${month} ${year}`;
            } else {
                return date.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                });
            }
        } catch (e) {
            console.error("Error formatting date:", e, timestamp);
            return t('notAvailable');
        }
    };

    // Get status color class
    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'delivered':
                return 'status-completed';
            case 'processing':
                return 'status-processing';
            case 'shipped':
                return 'status-shipped';
            case 'cancelled':
                return 'status-cancelled';
            case 'refunded':
                return 'status-refunded';
            case 'pending':
            default:
                return 'status-pending';
        }
    };

    // Format payment method
    const formatPaymentMethod = (method) => {
        return getPaymentMethodTranslation(language, method);
    };

    // Show order details in sidebar
    const showOrderDetails = (order) => {
        setSelectedOrder(order);
        setShowDetailsSidebar(true);
        // Prevent body scrolling when sidebar is open
        document.body.style.overflow = 'hidden';
    };

    // Close sidebar
    const closeDetailsSidebar = () => {
        setShowDetailsSidebar(false);
        // Allow body scrolling again
        document.body.style.overflow = "";
    };

    // Get customer name
    const getCustomerName = (order) => {
        if (!order) return 'N/A';

        if (order.customerInfo) {
            return `${order.customerInfo.firstName || ''} ${order.customerInfo.lastName || ''}`.trim();
        } else if (order.billingInfo) {
            return `${order.billingInfo.firstName || ''} ${order.billingInfo.lastName || ''}`.trim();
        } else if (order.shippingInfo) {
            return `${order.shippingInfo.firstName || ''} ${order.shippingInfo.lastName || ''}`.trim();
        }

        return t('guest');
    };

    // Get items count
    const getItemsCount = (order) => {
        if (!order) return 0;

        if (order.items && Array.isArray(order.items)) {
            return order.items.reduce((total, item) => total + (item.quantity || 1), 0);
        }

        if (order.details?.items && Array.isArray(order.details.items)) {
            return order.details.items.reduce((total, item) => total + (item.quantity || 1), 0);
        }

        return 0;
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{t('loadingOrders')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="error-icon">!</div>
                <h2>{t('error')}</h2>
                <p>{error}</p>
                <button onClick={() => window.location.reload()}>{t('tryAgain')}</button>
            </div>
        );
    }

    if (!activeStoreId) {
        return (
            <div className="error-container">
                <div className="error-icon">!</div>
                <h2>{t('noStoreSelected')}</h2>
                <p>{t('pleaseSelectStore')}</p>
                <button onClick={() => navigate('/')}>{t('goToHome')}</button>
            </div>
        );
    }

    return (
        <div className="order-history-container">
            <div className="order-history-header">
                <h1>{t('myOrders')}</h1>
            </div>

            {orders.length === 0 ? (
                <div className="no-orders">
                    <div className="no-orders-icon">📦</div>
                    <h2>{t('noOrdersFound')}</h2>
                    <p>{t('noOrdersPlaced')}</p>
                    <button onClick={() => navigate('/shop')}>{t('startShopping')}</button>
                </div>
            ) : (
                <div className="orders-list">
                    {orders.map((order) => (
                        <div
                            key={order.id}
                            className="order-card clickable"
                            onClick={() => showOrderDetails(order)}
                        >
                            <div className="order-header">
                                <div className="order-details">
                                    <h3>
                                        {order.orderNumber ?
                                            `${t('order')} #${order.orderNumber}` :
                                            `${t('order')} #${(order.orderId?.substring(0, 8) || order.id.substring(0, 8))}`
                                        }
                                    </h3>
                                    {order.orderNumber && (
                                        <p className="order-id-small">ID: {order.orderId?.substring(0, 8) || order.id.substring(0, 8)}</p>
                                    )}
                                    {!isMobile && (
                                        <p className="order-date">{t('placedOn')} {formatDate(order.createdAt)}</p>
                                    )}
                                </div>

                                <div className="order-status">
                                    <span className={`status-badge ${getStatusClass(order.status)}`}>
                                        {getStatusTranslation(language, order.status)}
                                    </span>
                                </div>
                            </div>

                            <div className="order-summary">
                                <div className="order-info">
                                    <div className="info-item">
                                        <span className="info-label">{t('total')}</span>
                                        <span className="info-value">{formatDisplay(order.total)}</span>
                                    </div>

                                    <div className="info-item">
                                        <span className="info-label">{t('items')}</span>
                                        <span className="info-value">{getItemsCount(order)}</span>
                                    </div>

                                    <div className="info-item">
                                        <span className="info-label">{t('payment')}</span>
                                        <span className="info-value">
                                            {formatPaymentMethod(order.paymentMethod || order.details?.paymentMethod)}
                                        </span>
                                    </div>
                                </div>

                                <div className="order-items-preview">
                                    {/* Show items preview from either items or details.items */}
                                    {(order.items && order.items.length > 0) ? (
                                        order.items.slice(0, 3).map((item, index) => (
                                            <div key={index} className="item-preview">
                                                {item.image ? (
                                                    <img src={item.image} alt={getLocalizedText(item.name, 'en')} className="itemH-image" />
                                                ) : (
                                                    <div className="itemH-image-placeholder"></div>
                                                )}
                                            </div>
                                        ))
                                    ) : (order.details?.items && order.details.items.length > 0) ? (
                                        order.details.items.slice(0, 3).map((item, index) => (
                                            <div key={index} className="item-preview">
                                                {item.image ? (
                                                    <img src={item.image} alt={getLocalizedText(item.name, 'en')} className="itemH-image" />
                                                ) : (
                                                    <div className="itemH-image-placeholder"></div>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="no-items-preview">{t('noItemsToDisplay')}</div>
                                    )}

                                    {/* Show more indicator if needed */}
                                    {(order.items && order.items.length > 3) && (
                                        <div className="more-items">+{order.items.length - 3}</div>
                                    )}
                                    {(!order.items && order.details?.items && order.details.items.length > 3) && (
                                        <div className="more-items">+{order.details.items.length - 3}</div>
                                    )}
                                </div>
                            </div>

                            {isMobile && (
                                <div className="order-summary-responsive">
                                    <div className="responsive-row">
                                        <span className="label">{t('order')}:</span>
                                        <span className="value">#{order.orderId?.substring(0, 8) || order.id.substring(0, 8)}</span>
                                    </div>
                                    <div className="responsive-row">
                                        <span className="label">{t('total')}:</span>
                                        <span className="value">{formatDisplay(order.total)}</span>
                                    </div>
                                    <div className="responsive-row">
                                        <span className="label">{t('date')}:</span>
                                        <span className="value">{formatDate(order.createdAt)}</span>
                                    </div>
                                    <div className="responsive-row">
                                        <span className="label">{t('items')}:</span>
                                        <span className="value">{getItemsCount(order)}</span>
                                    </div>
                                </div>
                            )}

                            <div className="order-actions">
                                {/* {order.status?.toLowerCase() === 'shipped' && (
                                    <button className="track-order-btn">{t('trackOrder')}</button>
                                )} */}
                                {order.invoice && (
                                    <button
                                        className="invoice-btn"
                                        onClick={() => window.open(order.invoice, '_blank')}
                                    >
                                        {t('viewInvoice')}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Order Details Sidebar - Now using the component */}
            <OrderDetailsSidebar
                selectedOrder={selectedOrder}
                showDetailsSidebar={showDetailsSidebar}
                closeDetailsSidebar={closeDetailsSidebar}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                getStatusClass={getStatusClass}
                formatPaymentMethod={formatPaymentMethod}
                getCustomerName={getCustomerName}
            />
        </div>
    );
};

export default OrderHistory;